import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { AppLayout, LoadingSpinner } from '../components/Layout';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Check, SkipForward, ArrowRight, ArrowLeft, Sparkles, Volume2, Square,
  Download, AlertTriangle, Compass, Wrench, Gauge, Minus, RotateCcw,
  MapPin, Home, Car, ShoppingBag, HeartPulse, Users, CreditCard, PiggyBank, Gift, Briefcase,
  Play, Clock, ShieldCheck,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import useNarration from '../hooks/useNarration';
import celebrate from '../utils/celebrate';
import LifeCareerEquation from '../components/LifeCareerEquation';
import LifeDesignResults from '../components/LifeDesignResults';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CM = `${API}/cost-model`;

const money = (n) => (n || n === 0 ? `$${Math.round(n).toLocaleString()}` : '-');

const ACCENT = {
  cyan: { hex: '#22d3ee', glass: 'glass-cyan' },
  violet: { hex: '#a78bfa', glass: 'glass-violet' },
  sky: { hex: '#60a5fa', glass: 'glass-cyan' },
  emerald: { hex: '#34d399', glass: 'glass-emerald' },
  rose: { hex: '#f472b6', glass: 'glass-violet' },
  amber: { hex: '#fbbf24', glass: 'glass-amber' },
  indigo: { hex: '#818cf8', glass: 'glass-violet' },
  teal: { hex: '#2dd4bf', glass: 'glass-emerald' },
  fuchsia: { hex: '#e879f9', glass: 'glass-violet' },
};
const accentOf = (k) => ACCENT[k] || ACCENT.cyan;
const FIT_HUE = { strong: '#34d399', partial: '#fbbf24', weak: '#f87171' };

const STEP_ICON = {
  place: MapPin, home: Home, move: Car, living: ShoppingBag, health: HeartPulse,
  people: Users, debt: CreditCard, future: PiggyBank, events: Gift, work: Briefcase,
};

const NARRATION_BASE = "Welcome to Lifestyle Design. This is where you design the life you actually want, "
  + "instead of picking a job and hoping it fits. You'll walk through it one piece at a time, where you'll live, "
  + "your own place, how you get around, everyday life, and how you want to spend your working days. "
  + "Skip anything that isn't you yet. When you're done, we'll show you the income that life takes, "
  + "and judge every career against both that number and what your assessment says about you.";

const narrationFor = (meta, count) => {
  if (meta?.mode === 'simple') {
    return `${NARRATION_BASE} One thing to know: you're on the shorter version, ${count} steps instead of ten, `
      + "because some of the full model is about a life you haven't lived yet. If you'd rather see all of it, "
      + "switch to the full version below. Let's begin.";
  }
  return `${NARRATION_BASE} You're on the full version, all ${count} steps, and none of them are compulsory. `
    + "If it feels like more than you need, switch to the shorter version below. Let's begin.";
};

// ── small pieces ─────────────────────────────────────────────────────────────
const Choice = ({ field, value, onChange, hex }) => {
  const options = field.options || [];
  // Long lists (like all 50 states) render as a compact dropdown, not a
  // wrapping chip grid, otherwise the page explodes on the first step.
  if (options.length > 8) {
    return (
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`select-${field.k}`}
        className="w-full bg-slate-900/70 border border-slate-700/70 rounded-xl py-2.5 px-3 text-white text-sm
                   focus:outline-none transition-colors duration-200"
        onFocus={(e) => { e.target.style.borderColor = hex; }}
        onBlur={(e) => { e.target.style.borderColor = 'rgba(51,65,85,0.7)'; }}
      >
        <option value="">Choose one…</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  return (
    <div className="flex flex-wrap gap-2" data-testid={`choice-${field.k}`}>
      {options.map((o) => {
        const on = value === o;
        return (
          <button key={o} type="button" onClick={() => onChange(on ? '' : o)}
            data-testid={`option-${field.k}-${o.replace(/\s+/g, '-').toLowerCase()}`}
            className="px-3.5 py-2 rounded-full text-sm border transition-all duration-200 hover:-translate-y-0.5"
            style={on
              ? { borderColor: hex, background: `${hex}26`, color: '#fff', boxShadow: `0 0 24px -8px ${hex}` }
              : { borderColor: 'rgba(148,163,184,0.28)', color: '#cbd5e1' }}>
            {o}
          </button>
        );
      })}
    </div>
  );
};

const NumberField = ({ field, value, onChange, hex }) => (
  <div className="relative">
    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 text-sm">
      {field.unit === '% of pay' || field.unit === 'months of expenses' ? '' : '$'}
    </span>
    <input type="number" min="0" inputMode="numeric" value={value ?? ''} placeholder="0"
      data-testid={`input-${field.k}`}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-slate-900/70 border border-slate-700/70 rounded-xl py-2.5 pl-8 pr-3 text-white
                 fp-num focus:outline-none transition-colors duration-200"
      onFocus={(e) => { e.target.style.borderColor = hex; }}
      onBlur={(e) => { e.target.style.borderColor = 'rgba(51,65,85,0.7)'; }} />
  </div>
);

const Bar = ({ label, value, max, hex, testid }) => (
  <div className="flex items-center gap-3" data-testid={testid}>
    <span className="w-44 shrink-0 text-xs text-slate-300">{label}</span>
    <div className="fp-meter flex-1">
      <div className="fp-meter-fill" style={{ width: `${Math.min(100, (value / Math.max(max, 1)) * 100)}%`, background: `linear-gradient(90deg,#22d3ee,${hex})`, boxShadow: `0 0 12px ${hex}66` }} />
    </div>
    <span className="w-24 text-right text-xs text-white fp-num">{money(value)}/mo</span>
  </div>
);

// ── page ─────────────────────────────────────────────────────────────────────
export default function LifestyleDesignPage() {
  const { getAuthHeader } = useAuth();
  const narration = useNarration();

  const [steps, setSteps] = useState([]);
  const [meta, setMeta] = useState(null);
  const [pre, setPre] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState([]);
  const [skipped, setSkipped] = useState([]);
  const [res, setRes] = useState(null);
  const [design, setDesign] = useState(null);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState('steps');   // intro · steps · results (skip intro)
  const [picks, setPicks] = useState({});        // step key → chosen quick pick
  const [showAdvanced, setShowAdvanced] = useState({}); // step key → show numeric fields
  const [resumeAt, setResumeAt] = useState(null);
  const resultsRef = useRef(null);

  const load = useCallback(async (mode) => {
    const [st, pf] = await Promise.all([
      axios.get(`${CM}/steps${mode ? `?mode=${mode}` : ''}`, getAuthHeader()),
      axios.get(`${CM}/prefill`, getAuthHeader()),
    ]);
    setSteps(st.data.steps);
    setMeta(st.data);
    setPre({ ...pf.data, state_reference: st.data.state_reference });
    const saved = pf.data.saved_answers || {};
    if (Object.keys(saved).length) {
      setAnswers(saved);
      setDone(pf.data.completed_steps || []);
      setSkipped(pf.data.skipped_steps || []);
      const settled = new Set([...(pf.data.completed_steps || []), ...(pf.data.skipped_steps || [])]);
      const first = st.data.steps.findIndex((s) => !settled.has(s.key));
      const at = first === -1 ? st.data.steps.length - 1 : first;
      setIdx(at);
      setResumeAt(st.data.steps[at]?.label || null);
    } else if (pf.data.known && Object.keys(pf.data.known).length) {
      setAnswers(pf.data.known);
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const loadDesign = useCallback(async () => {
    try { setDesign((await axios.get(`${API}/life-design`, getAuthHeader())).data); } catch { /* additive */ }
  }, [getAuthHeader]);

  const persist = useCallback(async (nextDone, nextSkipped, nextAnswers, loud) => {
    const payload = { answers: nextAnswers ?? answers, completed_steps: nextDone ?? done, skipped_steps: nextSkipped ?? skipped, save: true };
    try {
      const r = await axios.post(`${CM}/estimate`, payload, getAuthHeader());
      setRes(r.data);
      if (loud) {
        loadDesign();
        if (r.data.saved) toast.success('Saved. Everything on FuturePath now measures against this.');
        else toast.info('Saved your progress, answer a little more and we can set your number.');
      }
      return r.data;
    } catch {
      if (loud) toast.error('Could not work that out just now.');
      return null;
    }
  }, [answers, done, skipped, getAuthHeader, loadDesign]);

  useEffect(() => { loadDesign(); }, [loadDesign]);

  // If they've been here before, work their number out quietly so the readout and the
  // results view aren't empty when they arrive. Returning students who already have a
  // designed number should LAND on My Results, not the questionnaire.
  const primed = useRef(false);
  useEffect(() => {
    if (primed.current || !steps.length || Object.keys(answers).length < 3) return;
    primed.current = true;
    persist(done, skipped, answers, false).then((r) => {
      if (r?.required_gross) setPhase('results');
    });
  }, [steps.length, answers, done, skipped, persist]);

  const step = steps[idx];
  const isLast = idx === steps.length - 1;
  const settledCount = useMemo(() => {
    const keys = new Set(steps.map((s) => s.key));
    return new Set([...done, ...skipped].filter((k) => keys.has(k))).size;
  }, [done, skipped, steps]);

  // Progress is counted against the version they're actually on, not all 43 questions.
  const answeredOf = useMemo(() => {
    let answered = 0; let of = 0;
    steps.forEach((s) => {
      of += s.fields.length;
      answered += s.fields.filter((f) => answers[f.k] !== undefined && answers[f.k] !== '').length;
    });
    return { answered, of };
  }, [steps, answers]);

  const goTo = (i) => setIdx(Math.max(0, Math.min(steps.length - 1, i)));

  const next = async () => {
    const nd = [...new Set([...done, step.key])];
    const ns = skipped.filter((k) => k !== step.key);
    setDone(nd); setSkipped(ns);
    persist(nd, ns, answers, false);
    if (isLast) { await calculate(nd, ns); } else { goTo(idx + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }
  };

  const skip = async () => {
    const ns = [...new Set([...skipped, step.key])];
    const nd = done.filter((k) => k !== step.key);
    setSkipped(ns); setDone(nd);
    persist(nd, ns, answers, false);
    if (isLast) { await calculate(nd, ns); } else { goTo(idx + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }
  };

  const calculate = async (nd, ns) => {
    setBusy(true);
    const r = await persist(nd ?? done, ns ?? skipped, answers, true);
    setBusy(false);
    if (r) {
      if (r.saved) celebrate('That\u2019s the number your life takes. Everything on FuturePath now measures against it.');
      setPhase('results');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 120);
    }
  };

  const startOver = async () => {
    if (!window.confirm('Start over? This clears your answers and takes you back to the beginning.')) return;
    try { await axios.post(`${CM}/reset`, {}, getAuthHeader()); } catch { /* clear locally regardless */ }
    setAnswers({}); setDone([]); setSkipped([]); setRes(null); setDesign(null);
    setIdx(0); setResumeAt(null); setPhase('intro');
    toast.success('Cleared. Fresh start.');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const switchMode = async () => {
    const to = meta?.mode === 'simple' ? 'full' : 'simple';
    await load(to);
    setIdx(0);
    toast.success(to === 'full' ? 'Showing the full version.' : 'Showing the Student Quick version.');
  };

  const set = (k, v) => setAnswers((a) => ({ ...a, [k]: v }));

  if (!steps.length) return <AppLayout><div className="py-24 flex justify-center"><LoadingSpinner /></div></AppLayout>;

  const you = pre?.you || {};
  const started = settledCount > 0 || Object.keys(answers).length > 2;

  const ListenBtn = () => (
    <Button variant="outline" size="sm" data-testid="lifestyle-voice-btn"
      onClick={() => (narration.speaking ? narration.stop() : narration.say(narrationFor(meta, steps.length)))}
      className="border-slate-700 text-slate-200 rounded-full">
      {narration.loading ? <LoadingSpinner size="sm" />
        : narration.speaking ? <Square className="w-4 h-4 mr-2" /> : <Volume2 className="w-4 h-4 mr-2" />}
      {narration.speaking ? 'Stop' : 'Listen'}
    </Button>
  );

  const StartOverBtn = () => (
    <Button variant="ghost" size="sm" onClick={startOver} data-testid="start-over-btn"
      className="text-slate-300 hover:text-rose-300 rounded-full">
      <RotateCcw className="w-4 h-4 mr-1.5" />Start over
    </Button>
  );

  // ── INTRO ──────────────────────────────────────────────────────────────────
  if (phase === 'intro') {
    return (
      <AppLayout>
        <div className="relative overflow-hidden rounded-3xl border border-cyan-500/20 bg-[#0a1729]/60 p-8 md:p-12"
          data-testid="lifestyle-intro"
          style={{ boxShadow: '0 40px 120px -60px rgba(34,211,238,0.6)' }}>
          <div className="absolute -top-32 -right-24 w-[28rem] h-[28rem] rounded-full blur-3xl opacity-25"
            style={{ background: 'radial-gradient(circle, #22d3ee, transparent 65%)' }} />
          <div className="absolute -bottom-40 -left-24 w-[26rem] h-[26rem] rounded-full blur-3xl opacity-20"
            style={{ background: 'radial-gradient(circle, #a78bfa, transparent 65%)' }} />

          <div className="relative text-center max-w-4xl mx-auto">
            <motion.p initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className="text-[11px] uppercase tracking-[0.24em] text-cyan-300/90">
              Lifestyle Design
            </motion.p>
            <motion.h1 initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.06 }}
              className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight mt-3">
              Design the life you want.
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}
              className="text-base md:text-lg text-slate-300/90 mt-5 max-w-2xl mx-auto leading-relaxed">
              Most people choose work first and hope it covers the life they wanted. You'll do it the other
              way round, describe the life, and we'll work out the income it takes to live it. Then every
              career you look at gets measured against your life, not the other way round.
            </motion.p>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
              className="flex flex-wrap items-center justify-center gap-3 mt-9">
              <Button onClick={() => { setPhase('steps'); window.scrollTo({ top: 0 }); }} data-testid="intro-start-btn"
                className="rounded-full px-8 py-6 text-base text-white bg-gradient-to-r from-cyan-500 to-violet-500 hover:opacity-95">
                <Play className="w-4 h-4 mr-2" />
                {started ? `Pick up where I left off · ${resumeAt || steps[idx]?.label}` : 'Start designing'}
              </Button>
              <ListenBtn />
              {started && (
                <Button variant="outline" onClick={() => setPhase('results')} data-testid="intro-results-btn"
                  className="rounded-full border-slate-700 text-slate-200">See what I have so far</Button>
              )}
              {started && <StartOverBtn />}
            </motion.div>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.26 }}
              className="grid sm:grid-cols-3 gap-3 mt-10 max-w-3xl mx-auto" data-testid="intro-facts">
              {[
                { icon: Clock, head: `${steps.length} steps · about ${Math.max(6, steps.length * 2)} minutes`, sub: 'Go at your own pace' },
                { icon: SkipForward, head: 'Skip anything you want', sub: "Whatever isn't your life yet just gets left out" },
                { icon: ShieldCheck, head: 'Saves as you go', sub: 'Leave whenever, you come back to the same place' },
              ].map((f) => (
                <div key={f.head} className="rounded-2xl border border-slate-700/60 bg-slate-900/50 px-4 py-4">
                  <f.icon className="w-5 h-5 text-cyan-300 mx-auto" />
                  <p className="text-sm font-semibold text-white mt-2.5">{f.head}</p>
                  <p className="text-xs text-slate-300 mt-1">{f.sub}</p>
                </div>
              ))}
            </motion.div>

            {/* which version, said plainly, and choosable */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.32 }}
              className="mt-10 max-w-3xl mx-auto rounded-2xl border border-slate-700/60 bg-slate-900/40 p-5"
              data-testid="mode-note">
              <p className="text-sm font-semibold text-white">
                {meta?.mode === 'simple' ? "You're on the Student Quick version" : "You're on the full version"}
              </p>
              <p className="text-sm text-slate-300/85 mt-1.5 max-w-2xl mx-auto">
                {meta?.mode === 'simple'
                  ? `Just ${steps.length} quick steps, pick where you'll live, the kind of place, how you get around, everyday life, and how you want to work. Tap a preset and we fill in the typical numbers for you.`
                  : `All ${steps.length} steps, including the ones for people who depend on you, debt you already carry, and life events ahead. Nothing is compulsory.`}
              </p>
              <button onClick={switchMode} data-testid="switch-mode-btn"
                className="mt-3 inline-flex items-center gap-1.5 text-sm text-cyan-300 hover:text-cyan-100 transition-colors">
                {meta?.mode === 'simple' ? 'Show me all of it, the full version' : 'Switch to the Student Quick version'}
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </motion.div>
          </div>
        </div>

        {/* what we already know, nothing asked twice */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.32 }}
          className="glass-tint glass-cyan fp-rail p-5 mt-6" data-testid="lifestyle-known">
          <p className="text-[10px] uppercase tracking-[0.2em] text-cyan-300/85">What we already know about you</p>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {you.assessment?.code && (
              <span className="fp-pill" style={{ borderColor: '#a78bfa66', color: '#ddd6fe' }} data-testid="known-assessment">
                <Sparkles className="w-3 h-3" />{you.assessment.code}{you.assessment.headline ? ` · ${you.assessment.headline}` : ''}
              </span>
            )}
            {you.target_career && <span className="fp-pill" style={{ borderColor: '#34d39966', color: '#a7f3d0' }} data-testid="known-career">Target: {you.target_career}</span>}
            {(answers.state || you.state) && <span className="fp-pill" data-testid="known-state">{answers.state || you.state}</span>}
            {!you.assessment?.code && (
              <Link to="/app/assessment" className="fp-pill" style={{ borderColor: '#22d3ee66', color: '#a5f3fc' }} data-testid="known-assessment-missing">
                Take the assessment so we can judge fit, not just money <ArrowRight className="w-3 h-3" />
              </Link>
            )}
            {pre?.sources?.length > 0 && (
              <button onClick={() => { setAnswers((a) => ({ ...pre.known, ...a })); toast.success('Pulled in what we already had.'); }}
                className="fp-pill" style={{ borderColor: '#22d3ee66', color: '#a5f3fc' }} data-testid="prefill-btn">
                <Download className="w-3 h-3" />Pull in {pre.sources.map((s) => s.label).join(' · ')}
              </button>
            )}
          </div>
        </motion.div>

        {/* the journey map */}
        <div className="mt-10">
          <h2 className="font-outfit text-base md:text-lg font-semibold text-white text-center">What you'll walk through</h2>
          <p className="text-sm text-slate-300 mt-1 text-center">One piece at a time. Nothing here is a test.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-5" data-testid="journey-map">
            {steps.map((s, i) => {
              const a = accentOf(s.accent);
              const Icon = STEP_ICON[s.key] || Compass;
              const isDone = done.includes(s.key);
              const isSkipped = skipped.includes(s.key);
              return (
                <motion.button key={s.key} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.35 }}
                  onClick={() => { goTo(i); setPhase('steps'); window.scrollTo({ top: 0 }); }}
                  data-testid={`journey-card-${s.key}`}
                  className="text-left rounded-2xl border p-5 bg-slate-900/50 hover:-translate-y-1 transition-transform duration-200"
                  style={{ borderColor: `${a.hex}33`, boxShadow: `0 24px 60px -50px ${a.hex}` }}>
                  <div className="flex items-center justify-between">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ background: `${a.hex}1f`, border: `1px solid ${a.hex}44` }}>
                      <Icon className="w-5 h-5" style={{ color: a.hex }} />
                    </div>
                    {isDone ? <Check className="w-4 h-4" style={{ color: a.hex }} />
                      : isSkipped ? <Minus className="w-4 h-4 text-slate-300" />
                        : <span className="text-xs text-slate-300 fp-num">{String(i + 1).padStart(2, '0')}</span>}
                  </div>
                  <p className="font-outfit text-white font-semibold mt-4">{s.label}</p>
                  <p className="text-xs text-slate-300 mt-1.5 leading-relaxed">{s.why}</p>
                </motion.button>
              );
            })}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
          <Button onClick={() => { setPhase('steps'); window.scrollTo({ top: 0 }); }} data-testid="intro-start-bottom-btn"
            className="rounded-full px-7 text-white bg-gradient-to-r from-cyan-500 to-violet-500">
            {started ? 'Keep going' : 'Start with where you\'ll live'} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </AppLayout>
    );
  }

  // ── RESULTS ────────────────────────────────────────────────────────────────
  if (phase === 'results') {
    return (
      <AppLayout>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <button onClick={() => setPhase('steps')} data-testid="results-back-btn"
            className="inline-flex items-center gap-2 text-sm text-cyan-300 hover:gap-3 transition-all">
            <ArrowLeft className="w-4 h-4" />Back to my answers
          </button>
          <div className="flex items-center gap-2">
            <ListenBtn />
            <StartOverBtn />
          </div>
        </div>

        <div ref={resultsRef}>
          <LifeDesignResults
            res={res}
            design={design}
            getAuthHeader={getAuthHeader}
            onEditLifestyle={() => { setPhase('steps'); window.scrollTo({ top: 0 }); }}
          />
        </div>
      </AppLayout>
    );
  }

  // ── STEPS ──────────────────────────────────────────────────────────────────
  const acc = accentOf(step.accent);
  const stateRef = pre?.state_reference?.[Object.keys(pre?.state_reference || {}).find(
    (ab) => pre.state_reference[ab].name === answers.state)] || null;
  const answeredHere = step.fields.filter((f) => answers[f.k] !== undefined && answers[f.k] !== '').length;
  const StepIcon = STEP_ICON[step.key] || Compass;

  const applyPreset = (option) => {
    const factor = step.presets?.scale && stateRef?.index ? stateRef.index / 100 : 1;
    const filled = {};
    Object.entries(option.values).forEach(([k, v]) => {
      filled[k] = typeof v === 'number' ? Math.round(v * factor) : v;
    });
    setAnswers((a) => ({ ...a, ...filled }));
    setPicks((p) => ({ ...p, [step.key]: option.key }));
    toast.success(`Filled in typical numbers for "${option.label}". Change anything that isn't right.`);
  };

  return (
    <AppLayout>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button onClick={() => { setPhase('intro'); window.scrollTo({ top: 0 }); }} data-testid="back-to-overview-btn"
          className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-cyan-200 transition-colors">
          <ArrowLeft className="w-4 h-4" />Overview
        </button>
        <div className="flex items-center gap-2">
          {!!res?.required_gross && (
            <Button variant="outline" size="sm" onClick={() => setPhase('results')} data-testid="jump-results-btn"
              className="rounded-full border-slate-700 text-slate-200">My results</Button>
          )}
          <ListenBtn />
          <StartOverBtn />
        </div>
      </div>

      {/* progress */}
      <div className="mt-5" data-testid="lifestyle-progress">
        <div className="flex items-center justify-between text-[11px] text-slate-300 mb-2">
          <span>Step {idx + 1} of {steps.length} · {step.label}</span>
          <span className="fp-num">{settledCount} of {steps.length} done</span>
        </div>
        <div className="fp-meter">
          <div className="fp-meter-fill transition-all duration-500"
            style={{ width: `${Math.round(((idx + 1) / steps.length) * 100)}%`, background: `linear-gradient(90deg,#22d3ee,${acc.hex})` }} />
        </div>
        <div className="flex flex-wrap gap-1.5 mt-3" data-testid="lifestyle-rail">
          {steps.map((s, i) => {
            const a = accentOf(s.accent);
            const on = i === idx;
            const isSkipped = skipped.includes(s.key);
            return (
              <button key={s.key} onClick={() => goTo(i)} data-testid={`rail-${s.key}`} title={s.label}
                className="px-2.5 py-1 rounded-full text-[11px] border flex items-center gap-1 transition-all duration-200 hover:-translate-y-0.5"
                style={on ? { borderColor: a.hex, background: `${a.hex}22`, color: '#fff', boxShadow: `0 0 26px -8px ${a.hex}` }
                  : isSkipped ? { borderColor: 'rgba(148,163,184,0.2)', color: '#C6D4E8' }
                    : done.includes(s.key) ? { borderColor: `${a.hex}59`, color: '#e2e8f0' }
                      : { borderColor: 'rgba(148,163,184,0.18)', color: '#94a3b8' }}>
                {done.includes(s.key) ? <Check className="w-3 h-3" style={{ color: a.hex }} />
                  : isSkipped ? <Minus className="w-3 h-3" /> : <span className="fp-num">{i + 1}</span>}
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        {/* the step itself */}
        <div className={res?.required_gross ? 'lg:col-span-2' : 'lg:col-span-3'}>
          <AnimatePresence mode="wait">
            <motion.div key={step.key} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.28 }} className={`glass-tint ${acc.glass} fp-rail p-6 md:p-7`} data-testid="lifestyle-step">
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                    style={{ background: `${acc.hex}1f`, border: `1px solid ${acc.hex}44` }}>
                    <StepIcon className="w-5 h-5" style={{ color: acc.hex }} />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em]" style={{ color: acc.hex }}>
                      Step {idx + 1} of {steps.length}{step.kind === 'prefs' ? ' · the work itself' : ''}
                    </p>
                    <h2 className="font-outfit text-2xl font-semibold text-white mt-1.5">{step.label}</h2>
                    <p className="text-sm text-slate-300/80 mt-2 max-w-xl">{step.why}</p>
                  </div>
                </div>
                <span className="text-[11px] text-slate-300 fp-num">{answeredHere}/{step.fields.length} answered</span>
              </div>

              {step.key === 'place' && stateRef && (
                <div className="mt-5 rounded-2xl p-4 border" data-testid="state-reference"
                  style={{ borderColor: `${acc.hex}55`, background: `linear-gradient(120deg, ${acc.hex}1f, rgba(15,23,42,0.5))`, boxShadow: `0 0 40px -22px ${acc.hex}` }}>
                  <p className="text-[10px] uppercase tracking-[0.18em]" style={{ color: acc.hex }}>
                    What {answers.state} actually costs
                  </p>
                  <div className="flex flex-wrap gap-8 mt-3">
                    <div><p className="text-[11px] text-slate-300/80">Typical rent</p>
                      <p className="text-xl font-bold text-white fp-num">{money(stateRef.rent)}<span className="text-xs text-slate-300 font-normal">/mo</span></p></div>
                    <div><p className="text-[11px] text-slate-300/80">Typical pay there</p>
                      <p className="text-xl font-bold text-white fp-num">{money(stateRef.median_pay)}<span className="text-xs text-slate-300 font-normal">/yr</span></p></div>
                    <div><p className="text-[11px] text-slate-300/80">Cost of living</p>
                      <p className="text-xl font-bold fp-num" style={{ color: stateRef.index > 105 ? '#fbbf24' : '#34d399' }}>
                        {stateRef.index}%<span className="text-xs text-slate-300 font-normal"> of the US average</span></p></div>
                  </div>
                </div>
              )}

              {step.presets && (
                <div className="mt-6" data-testid={`presets-${step.key}`}>
                  <p className="text-sm font-semibold text-white">{step.presets.label}</p>
                  <p className="text-xs text-slate-300 mt-0.5">
                    Pick the closest one and we'll fill in typical numbers{stateRef ? ` for ${answers.state}` : ''}, change anything that's wrong.
                  </p>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5 mt-3">
                    {step.presets.options.map((o) => {
                      const on = picks[step.key] === o.key;
                      return (
                        <button key={o.key} type="button" onClick={() => applyPreset(o)}
                          data-testid={`preset-${step.key}-${o.key}`}
                          className="text-left rounded-xl border px-3.5 py-3 transition-all duration-200 hover:-translate-y-0.5"
                          style={on
                            ? { borderColor: acc.hex, background: `${acc.hex}1f`, boxShadow: `0 0 26px -10px ${acc.hex}` }
                            : { borderColor: 'rgba(148,163,184,0.22)' }}>
                          <p className="text-sm font-medium text-white">{o.label}</p>
                          <p className="text-[11px] text-slate-300 mt-0.5">{o.blurb}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-x-6 gap-y-5 mt-6">
                {(() => {
                  // If a preset is picked, tuck the individual dollar fields behind
                  // an "Adjust the numbers" accordion, for a teen, the preset alone
                  // is enough. Choice fields (dropdowns) always stay visible.
                  const presetPicked = !!picks[step.key] && !!step.presets;
                  const numericFields = step.fields.filter((f) => f.type !== 'choice');
                  const choiceFields = step.fields.filter((f) => f.type === 'choice');
                  const visible = presetPicked
                    ? [...choiceFields, ...(showAdvanced[step.key] ? numericFields : [])]
                    : step.fields;
                  return visible.map((f) => (
                    <div key={f.k} className={f.type === 'choice' ? 'md:col-span-2' : ''} data-testid={`field-${f.k}`}>
                      <label className="block text-xs uppercase tracking-wider text-slate-300 mb-2">
                        {f.label}
                        {f.unit && <span className="text-slate-300 normal-case tracking-normal"> · {f.unit}</span>}
                      </label>
                      {f.type === 'choice'
                        ? <Choice field={f} value={answers[f.k] ?? ''} onChange={(v) => set(f.k, v)} hex={acc.hex} />
                        : <NumberField field={f} value={answers[f.k]} onChange={(v) => set(f.k, v)} hex={acc.hex} />}
                      {f.hint && <p className="text-[11px] text-slate-300 mt-1.5">{f.hint}</p>}
                    </div>
                  ));
                })()}
                {!!picks[step.key] && !!step.presets && step.fields.some((f) => f.type !== 'choice') && (
                  <button
                    type="button"
                    onClick={() => setShowAdvanced((s) => ({ ...s, [step.key]: !s[step.key] }))}
                    data-testid={`toggle-advanced-${step.key}`}
                    className="md:col-span-2 self-start text-xs text-cyan-300 hover:text-cyan-100 inline-flex items-center gap-1 transition-colors"
                  >
                    {showAdvanced[step.key]
                      ? 'Hide the individual numbers'
                      : 'Adjust the numbers →'}
                  </button>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-3 mt-8 pt-5 border-t border-white/10">
                {idx > 0 && (
                  <Button variant="ghost" onClick={() => goTo(idx - 1)} data-testid="step-back-btn" className="text-slate-300">
                    <ArrowLeft className="w-4 h-4 mr-1.5" />Back
                  </Button>
                )}
                <Button onClick={next} data-testid="step-next-btn" className="rounded-full text-white"
                  style={{ background: `linear-gradient(90deg, ${acc.hex}, #22d3ee)` }}>
                  {isLast ? 'Finish and see my life' : 'Next'}<ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
                <Button variant="outline" onClick={skip} data-testid="step-skip-btn" className="border-slate-700 text-slate-300 rounded-full">
                  <SkipForward className="w-4 h-4 mr-1.5" />Not me, skip it
                </Button>
                <Button variant="ghost" onClick={() => calculate()} disabled={busy} data-testid="calculate-now-btn" className="text-cyan-300">
                  {busy ? 'Working…' : 'Show me what I have so far'}
                </Button>
              </div>
              <p className="text-[11px] text-slate-300 mt-3">
                {step.skip_hint || 'Everything saves as you go. Stop whenever you like and pick it up later.'}
              </p>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* live readout, only once there's something real to show */}
        {!!res?.required_gross && (
          <div className="space-y-5">
            <div className="glass-tint glass-emerald fp-rail p-6" data-testid="live-number">
              <p className="text-[10px] uppercase tracking-[0.2em] text-emerald-300/85">So far, this life takes</p>
              <p className="font-outfit text-4xl font-bold text-white fp-num mt-2" data-testid="live-required">
                {money(res.required_gross)}
              </p>
              <p className="text-xs text-slate-300/80 mt-1.5">
                ${res.hourly_equivalent?.toFixed(2)}/hour · {money(res.monthly_with_savings)}/month all in · {res.effective_tax_rate}% effective tax
              </p>
              <p className="text-[11px] text-slate-300 mt-2">Built from the {settledCount} step{settledCount === 1 ? '' : 's'} you've answered, it moves as you go.</p>
              {res.confidence && (
                <div className="mt-4">
                  <div className="flex items-center justify-between text-[11px] text-slate-300 mb-1.5">
                    <span>{answeredOf.answered} of {answeredOf.of} answered</span>
                    <span className="text-emerald-300">{res.confidence.level}</span>
                  </div>
                  <div className="fp-meter"><div className="fp-meter-fill" style={{ width: `${Math.round((answeredOf.answered / Math.max(answeredOf.of, 1)) * 100)}%` }} /></div>
                </div>
              )}
              {res.per_step?.some((p) => p.monthly > 0) && (
                <div className="mt-5 space-y-2">
                  {res.per_step.filter((p) => p.monthly > 0).map((p) => (
                    <div key={p.key} className="flex items-center justify-between text-xs">
                      <span className="text-slate-300">{p.label}</span>
                      <span className="text-white fp-num">{money(p.monthly)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {res.missing_next?.filter((m) => steps.some((s) => s.key === m.key)).length > 0 && (
              <div className="glass-tint glass-violet p-5" data-testid="still-missing">
                <p className="text-[10px] uppercase tracking-[0.2em] text-violet-300/85">Firm the number up</p>
                <div className="space-y-2 mt-3">
                  {res.missing_next.filter((m) => steps.some((s) => s.key === m.key)).map((m) => (
                    <button key={m.key} onClick={() => goTo(steps.findIndex((s) => s.key === m.key))}
                      className="fp-tile p-3 w-full text-left" data-testid={`goto-${m.key}`}>
                      <p className="text-sm text-white">{m.step}</p>
                      <p className="text-[11px] text-slate-300">{m.left} question{m.left === 1 ? '' : 's'} left</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {design?.income_floor && (
              <div className="glass-tint glass-cyan p-5" data-testid="floor-in-use">
                <p className="text-[10px] uppercase tracking-[0.2em] text-cyan-300/85">In use across FuturePath</p>
                <p className="text-sm text-slate-300/85 mt-2">
                  Your number of <span className="text-white fp-num">{money(design.income_floor)}</span> is what your roadmap,
                  Learning ROI, career matches and report are measured against.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
